import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import useApiFromGetPost from "../utils/useApiGetPost"; // adjust path to your actual hook

function formatCount(n) {
  return new Intl.NumberFormat().format(n);
}

const PAGE_SIZE = 5; // Reduced page size for compact layout viewports
const BUCKETS = [
  { key: "active", label: "Active", max: 30, color: "#2ECC71" },
  { key: "at_risk", label: "At risk", max: 60, color: "#FFD23F" },
  { key: "churning", label: "Churning", max: 90, color: "#FFA62B" },
  { key: "churned", label: "Churned", max: Infinity, color: "#FF5C7C" },
];

function daysSince(dateStr, now) {
  const then = new Date(dateStr);
  const diffMs = now.setHours(0, 0, 0, 0) - new Date(then).setHours(0, 0, 0, 0);
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function formatDuration(dateStr, now) {
  const from = new Date(dateStr);
  const to = new Date(now);

  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let days = to.getDate() - from.getDate();

  if (days < 0) {
    months -= 1;
    const prevMonth = new Date(to.getFullYear(), to.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (years > 0) {
    return months > 0 ? `${years}y ${months}m` : `${years}y`;
  }
  if (months > 0) {
    return days > 0 ? `${months}m ${days}d` : `${months}m`;
  }
  return `${days}d`;
}

function bucketFor(days) {
  return BUCKETS.find((b) => days <= b.max) || BUCKETS[BUCKETS.length - 1];
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function TotalsTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const { ChurnMonth, ChurnedCustomers } = payload[0].payload;
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e5e5e5",
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, color: "#333" }}>{ChurnMonth}</div>
      <div style={{ color: "#666" }}>{formatCount(ChurnedCustomers)} churned</div>
    </div>
  );
}

export default function ChurnAnalysis({
  baseUrl,
  company_code_from_local_storage,
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Instance 1: Graph data API
  const {
    loading: loadingChurnGraph,
    data: apiChurnGraphData,
    get: getChurnGraph,
  } = useApiFromGetPost();

  // Instance 2: Customer list API 
  const {
    loading: loadingCustomers,
    data: apiCustomersData,
    get: getCustomers,
  } = useApiFromGetPost();

  // Parse Year Chart Data safely
  const totalsData = useMemo(() => {
    if (!apiChurnGraphData) return [];
    if (Array.isArray(apiChurnGraphData)) return apiChurnGraphData;
    if (Array.isArray(apiChurnGraphData.data)) return apiChurnGraphData.data;
    return [];
  }, [apiChurnGraphData]);

  // Dynamically calculate the minimum ChurnMonth year to establish dynamic fromDate
  const calculatedFromDate = useMemo(() => {
    if (!totalsData.length) return "2000-01-01"; 

    const years = totalsData
      .map((item) => parseInt(String(item.ChurnMonth).substring(0, 4), 10))
      .filter((year) => !isNaN(year));

    if (!years.length) return "2000-01-01";

    const minYear = Math.min(...years);
    return `${minYear}-01-01`;
  }, [totalsData]);

  const buildChurnGraphUrl = useCallback(() => {
    const companyCode = company_code_from_local_storage || "-";
    const fromDate = "2000-01-01";
    const toDate = new Date().toISOString().split("T")[0];
    return `${baseUrl}/api/HRDashboard/${companyCode}/-/CRM_CHURN_ANALYSISGRAPH/${fromDate}/${toDate}/-/-/-/-/-`;
  }, [baseUrl, company_code_from_local_storage]);

  const buildCustomersUrl = useCallback(() => {
    const companyCode = company_code_from_local_storage || "-";
    const toDate = new Date().toISOString().split("T")[0];
    return `${baseUrl}/api/HRDashboard/${companyCode}/-/CRM_CHURN_ANALYSIS/${calculatedFromDate}/${toDate}/-/-/-/-/-`;
  }, [baseUrl, company_code_from_local_storage, calculatedFromDate]);

  // Initial load for Graph data
  useEffect(() => {
    getChurnGraph(buildChurnGraphUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger customer query once calculatedFromDate updates via totalsData response
  useEffect(() => {
    if (totalsData.length > 0) {
      getCustomers(buildCustomersUrl());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedFromDate, totalsData]);

  // Parse Raw Customer List Data safely
  const rawCustomers = useMemo(() => {
    if (!apiCustomersData) return [];
    if (Array.isArray(apiCustomersData)) return apiCustomersData;
    if (Array.isArray(apiCustomersData.data)) return apiCustomersData.data;
    return [];
  }, [apiCustomersData]);

  const now = useMemo(() => new Date(), []);

  // Enrich data arrays
  const enriched = useMemo(
    () =>
      rawCustomers.map((c) => {
        const days = daysSince(c.Last_Order, new Date(now));
        const bucket = bucketFor(days);
        const durationLabel = formatDuration(c.Last_Order, now);
        return { ...c, daysSince: days, bucket, durationLabel };
      }),
    [rawCustomers, now]
  );

  const churnedCount = enriched.filter((c) => c.bucket.key === "churned").length;
  const churnRate = rawCustomers.length
    ? ((churnedCount / rawCustomers.length) * 100).toFixed(1)
    : "0.0";

  // Front-end Filtering
  const visibleCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? enriched.filter(
          (c) =>
            (c.CustomerName && c.CustomerName.toLowerCase().includes(q)) ||
            String(c.CustomerID).toLowerCase().includes(q)
        )
      : enriched;
    return filtered.slice().sort((a, b) => b.daysSince - a.daysSince);
  }, [enriched, search]);

  const totalPages = Math.max(1, Math.ceil(visibleCustomers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  
  const pagedCustomers = useMemo(
    () =>
      visibleCustomers.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
      ),
    [visibleCustomers, currentPage]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%", 
        minWidth: 0,           
        margin: "0 auto",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        background: "#fff",
        borderRadius: 16,
        padding: "12px", 
        boxSizing: "border-box",
        overflow: "hidden",     
        marginBottom:"100px"
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "1rem", width: "100%" }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "#2a2a2a" }}>
          Customer churn
        </h2>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
          {rawCustomers.length} customers · {churnedCount} churned ({churnRate}%)
        </p>
      </div>

      {/* Yearly totals */}
      <div style={{ marginBottom: "1.25rem", width: "100%", overflow: "hidden" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#999",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: 6,
          }}
        >
          Churned customers by year
          {loadingChurnGraph && (
            <span style={{ marginLeft: 6, color: "#bbb", fontWeight: 500 }}>
              Loading Chart...
            </span>
          )}
        </div>
        
        <div style={{ width: "100%", maxWith: "100%", minWidth: 0, overflow: "hidden" }}>
          <div style={{ width: "100%", height: 130, position: "relative" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={totalsData}
                margin={{ top: 8, right: 4, left: -24, bottom: 0 }}
              >
                <CartesianGrid vertical={false} stroke="#f0f0f0" />
                <XAxis
                  dataKey="ChurnMonth"
                  tick={{ fontSize: 10, fill: "#888" }}
                  axisLine={{ stroke: "#eee" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tickFormatter={formatCount}
                  tick={{ fontSize: 9, fill: "#888" }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip content={<TotalsTooltip />} cursor={{ fill: "#fafafa" }} />
                <Bar
                  dataKey="ChurnedCustomers"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                  fill="#9B5DE5"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Front-end Search Inputs */}
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column", 
            gap: 8,
            marginBottom: 12,
            width: "100%"
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#999",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            All customers · {visibleCustomers.length}
            {loadingCustomers && <span style={{ marginLeft: 6, color: "#bbb" }}> (Refreshing...)</span>}
          </div>

          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search name or ID..."
            style={{
              fontSize: 12,
              padding: "7px 10px",
              borderRadius: 8,
              border: "1px solid #eee",
              outline: "none",
              width: "100%", 
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Scrollable Fixed Height Table Body Content */}
        <div 
          style={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: 6, 
            width: "100%",
            maxHeight: "240px", // Strict viewport constraint
            overflowY: "auto",  // Generates safe inner scroll
            paddingRight: "2px"
          }}
        >
          {pagedCustomers.length === 0 ? (
            <p style={{ fontSize: 12, color: "#aaa", padding: "8px 0" }}>
              {loadingCustomers ? "Loading customers..." : "No customers match."}
            </p>
          ) : (
            pagedCustomers.map((c) => (
              <div
                key={c.CustomerID}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#fafafa",
                  width: "100%",
                  boxSizing: "border-box",
                  minWidth: 0 
                }}
              >
                <div style={{ minWidth: 0, overflow: "hidden" }}> 
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#333",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      display: "block"
                    }}
                    title={c.CustomerName}
                  >
                    {c.CustomerName || "Unknown Name"}
                  </div>
                  <div 
                    style={{ 
                      fontSize: 10, 
                      color: "#999",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      display: "block"
                    }}
                  >
                    #{c.CustomerID} · last {c.Last_Order ? formatDate(c.Last_Order) : "N/A"}
                  </div>
                </div>
                
                <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "3px 6px",
                      borderRadius: 999,
                      color: c.bucket?.color || "#777",
                      background: `${c.bucket?.color || "#777"}20`,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {c.durationLabel}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls attached below fixed height container */}
        {visibleCustomers.length > PAGE_SIZE && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid #f0f0f0",
              gap: 4,
              width: "100%"
            }}
          >
            <span style={{ fontSize: 11, color: "#999", flexShrink: 0 }}>
              Pg {currentPage}/{totalPages}
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  fontSize: 11,
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid #eee",
                  background: "#fff",
                  color: currentPage === 1 ? "#ccc" : "#555",
                  cursor: currentPage === 1 ? "default" : "pointer",
                }}
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  fontSize: 11,
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid #eee",
                  background: "#fff",
                  color: currentPage === totalPages ? "#ccc" : "#555",
                  cursor: currentPage === totalPages ? "default" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}